class ProductInfo extends HTMLElement {
  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  activeFetch = null;

  constructor() {
    super();
  }
  
  get productForm() {
    return this.querySelector(`product-form`);
  }

  connectedCallback() {
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.querySelector('.product-info__variant-selects')?.addEventListener('change', this.handleVariantChange);
    this.querySelector('.fixed-atb__option')?.addEventListener('change', this.handleVariantChange);
  }
  
  disconnectedCallback() {
    this.querySelector('.product-info__variant-selects')?.removeEventListener('change', this.handleVariantChange);
    this.querySelector('.fixed-atb__option')?.removeEventListener('change', this.handleVariantChange);
  }

  handleVariantChange(event) {
    this.resetProductFormState();

    const selectedOptionValues = Array.from(this.querySelectorAll('fieldset input:checked')).map(({ dataset }) => dataset.optionValueId);

    const productUrl = event.target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
    this.pendingRequestUrl = productUrl;
    const shouldFetchFullPage = this.dataset.url !== productUrl;

    this.renderProductInfo({
      requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
      targetId: event.target.id,
      callback: shouldFetchFullPage
        ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
        : this.handleUpdateProductInfo(productUrl),
    });
  }

  resetProductFormState() {
    this.productForm?.toggleSubmitButton(true);
    this.productForm?.handleErrorMessage();
  }

  buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
    const params = [];

    !shouldFetchFullPage && params.push(`section_id=${this.dataset.section}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.activeFetch?.abort();
    this.activeFetch = new AbortController();

    fetch(requestUrl, { signal: this.activeFetch.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        callback(html);
      })
      .then(() => {
        // set focus to last clicked option value
        document.querySelector(`#${targetId}`)?.focus();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  handleSwapProduct(productUrl, updateFullPage) {
    return (html) => {
      const variant = this.getSelectedVariant(html.querySelector('product-info'));
      this.updateURL(productUrl, variant?.id);

      if (updateFullPage) {
        document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

        // might have to change later if add scripts to product page since by default, scripts are disabled when using element.innerHTML.
        document.querySelector('main').innerHTML = html.querySelector('main').innerHTML;
      } else {
        document.querySelector('product-info').innerHTML = html.querySelector('product-info').innerHTML;
        document.querySelector('fixed-atb').innerHTML = html.querySelector('fixed-atb').innerHTML;
      }
    };
  }

  handleUpdateProductInfo(productUrl) {
    return (html) => {
      const variant = this.getSelectedVariant(html);
      this.updateURL(productUrl, variant?.id);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (variant?.featured_media?.id != this.getSelectedVariant(this)?.featured_media?.id) {
        this.updateMedia(html);
      }


      this.updateOptionValues(html);
      this.updateVariantInputs(variant?.id);
      this.updateHeader(html);
      this.updateForms(html);
      this.updateFixedATB(html);
    };
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector('[data-selected-variant]')?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  updateURL(url, variantId) {
    window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
  }

  updateOptionValues(html) {
    const variantSelects = html.querySelector('.product-info__variant-selects');
    if (variantSelects && document.querySelector('.product-info__variant-selects')) {
      document.querySelector('.product-info__variant-selects').innerHTML = variantSelects.innerHTML;
    }
  }

  updateVariantInputs(variantId) {
    document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach((productForm) => {
      productForm.querySelector('input[name="id"]').value = variantId ?? '';
    });
  }

  updateHeader(html) {
    const price = html.querySelector('.product-info__header');
    if (price && document.querySelector('.product-info__header')) {
      document.querySelector('.product-info__header').innerHTML = price.innerHTML;
    }
  }

  updateForms(html) {
    const price = html.querySelector('.product-info__form');
    if (price && document.querySelector('.product-info__form')) {
      document.querySelector('.product-info__form').innerHTML = price.innerHTML;
    }
  }

  updateMedia(html) {
    const mediaGallery = html.querySelector(".product-info__gallery");
    if (mediaGallery && document.querySelector('.product-info__gallery')) {
      document.querySelector('.product-info__gallery').classList.add('opacity-0');
      document.querySelector('.product-info__gallery').addEventListener('transitionend', () => {
        document.querySelector('.product-info__gallery').innerHTML = mediaGallery.innerHTML;
        document.querySelector('.product-info__gallery').classList.remove('opacity-0');
      }, {once: true});
    }
  }

  updateFixedATB(html) {
    const fixedATB = html.querySelector("fixed-atb");
    if (fixedATB && document.querySelector('fixed-atb')) {
      document.querySelector('fixed-atb').innerHTML = fixedATB.innerHTML;
    }

  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(true, 'Unavailable');
  }
}

customElements.define('product-info', ProductInfo);
